// Range bar for active wind cycle — bounds from tp_price + SL formula.
// Hidden when no active cycle (open/closing with entry_price).
// In chasing-maker state: a minimal cyan marker replaces the full range view.
// Active-cycle visual is delegated to the shared RangeBar (legacy .range-bar).
import type { WindSession, WindCycle } from "@/types/api";
import { fmt } from "@/lib/format";
import RangeBar from "@/components/common/range-bar";

interface WindRangeBarProps {
  session: WindSession;
  latestCycle: WindCycle | null | undefined;
  livePrice: number | null;
}

function computeBounds(
  cycle: WindCycle,
  session: WindSession
): { lower: number; upper: number } {
  const entry = cycle.entry_price!;
  const tpPrice =
    cycle.tp_price ??
    (cycle.direction === "long"
      ? entry * (1 + session.tp_pct / 100)
      : entry * (1 - session.tp_pct / 100));
  const slPrice =
    cycle.direction === "long"
      ? entry * (1 - session.sl_pct / 100)
      : entry * (1 + session.sl_pct / 100);
  return {
    lower: Math.min(tpPrice, slPrice),
    upper: Math.max(tpPrice, slPrice),
  };
}

export default function WindRangeBar({
  session,
  latestCycle,
  livePrice,
}: WindRangeBarProps) {
  const cycleStatus = latestCycle?.status;
  const hasActiveCycle =
    (cycleStatus === "open" || cycleStatus === "closing") &&
    latestCycle?.entry_price;

  // Chasing-maker state: show single cyan marker at entry_limit_price
  const isChasing =
    cycleStatus === "pending" && latestCycle?.entry_limit_price;

  if (!hasActiveCycle && !isChasing) return null;

  if (isChasing && latestCycle?.entry_limit_price) {
    const limitPrice = latestCycle.entry_limit_price;
    return (
      <div className="mb-4 px-1">
        <div className="flex justify-between text-xs text-muted mb-1">
          <span className="text-cyan-400">
            post-only @ {fmt(limitPrice, session.symbol)} · chasing maker
          </span>
          {livePrice != null && livePrice > 0 && (
            <span className="text-warn">{fmt(livePrice, session.symbol)}</span>
          )}
        </div>
        <div className="relative h-1.5 bg-border rounded-full overflow-visible">
          <div
            className="absolute top-1/2 w-2.5 h-2.5 rounded-full border-2 border-bg shadow"
            style={{
              left: "50%",
              transform: "translateX(-50%) translateY(-50%)",
              background: "#06b6d4",
            }}
          />
        </div>
      </div>
    );
  }

  if (!hasActiveCycle || !latestCycle?.entry_price) return null;

  const { lower, upper } = computeBounds(latestCycle, session);
  const price = livePrice ?? latestCycle.entry_price;

  return (
    <RangeBar
      symbol={session.symbol}
      lower={lower}
      upper={upper}
      current={price}
    />
  );
}
